import React, { useMemo, useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Tabs } from '../ui/Tabs';
import { Search, Filter, Plus, Edit2, Trash2, Calendar, Globe, Sparkles, Copy, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Forecast, ForecastType, CmsStatus, SupportedLang, ZodiacSign, ZODIAC_SIGNS, FORECAST_TYPE_LABELS, STATUS_LABELS, formatDateRu, createEmptyForecast, getZodiacLabel, getZodiacSymbol } from '../../data/forecastCmsData';
interface AdminForecastsManagerProps {
  forecasts: Forecast[];
  setForecasts: React.Dispatch<React.SetStateAction<Forecast[]>>;
  success: (msg: string) => void;
  renderTableHeader: (title: string, count: number, onExport?: () => void, onCreate?: () => void, createLabel?: string) => React.ReactNode;
}
export function AdminForecastsManager({
  forecasts,
  setForecasts,
  success,
  renderTableHeader
}: AdminForecastsManagerProps) {
  const [activeTab, setActiveTab] = useState('day');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSign, setFilterSign] = useState<ZodiacSign | 'all' | 'general'>('all');
  const [filterStatus, setFilterStatus] = useState<CmsStatus | 'all'>('all');
  const [editingForecast, setEditingForecast] = useState<Forecast | null>(null);
  // Filter forecasts
  const filteredForecasts = useMemo(() => {
    return forecasts.filter(f => {
      // Tab filter (type)
      if (activeTab !== 'all' && activeTab !== 'archive' && f.forecast_type !== activeTab) return false;
      if (activeTab === 'archive' && f.status !== 'archived') return false;
      if (activeTab !== 'archive' && f.status === 'archived') return false;
      // Search filter
      if (searchQuery && !f.title.toLowerCase().includes(searchQuery.toLowerCase()) && !f.slug.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Sign filter
      if (filterSign === 'general' && f.scope !== 'general') return false;
      if (filterSign !== 'all' && filterSign !== 'general' && f.zodiac_sign !== filterSign) return false;
      // Status filter
      if (filterStatus !== 'all' && f.status !== filterStatus) return false;
      return true;
    }).sort((a, b) => new Date(b.date_start).getTime() - new Date(a.date_start).getTime());
  }, [forecasts, activeTab, searchQuery, filterSign, filterStatus]);
  const tabs = [{
    id: 'day',
    label: 'Дневные'
  }, {
    id: 'week',
    label: 'Недельные'
  }, {
    id: 'month',
    label: 'Месячные'
  }, {
    id: 'all',
    label: 'Все прогнозы'
  }, {
    id: 'archive',
    label: 'Архив'
  }];
  const handleStatusChange = (id: string, newStatus: CmsStatus) => {
    setForecasts(forecasts.map(f => f.id === id ? {
      ...f,
      status: newStatus,
      updated_at: new Date().toISOString()
    } : f));
    success(`Статус изменен на "${STATUS_LABELS[newStatus].ru}"`);
  };
  const handleDelete = (id: string) => {
    if (confirm('Вы уверены, что хотите удалить этот прогноз?')) {
      setForecasts(forecasts.filter(f => f.id !== id));
      success('Прогноз удален');
    }
  };
  const handleDuplicate = (forecast: Forecast) => {
    const duplicated = {
      ...forecast,
      id: `frc-${Date.now()}`,
      title: `${forecast.title} (Копия)`,
      slug: `${forecast.slug}-copy-${Date.now().toString().slice(-4)}`,
      status: 'draft' as CmsStatus,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      publish_at: null,
      hide_at: null,
      views: 0
    };
    setForecasts([duplicated, ...forecasts]);
    success('Прогноз дублирован');
  };
  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingForecast) return;
    if (forecasts.some(f => f.id === editingForecast.id)) {
      // Update
      setForecasts(forecasts.map(f => f.id === editingForecast.id ? {
        ...editingForecast,
        updated_at: new Date().toISOString()
      } : f));
      success('Прогноз обновлен');
    } else {
      // Create
      setForecasts([editingForecast, ...forecasts]);
      success('Прогноз создан');
    }
    setEditingForecast(null);
  };
  const handleCreateNew = () => {
    setEditingForecast(createEmptyForecast(activeTab === 'all' || activeTab === 'archive' ? 'day' : activeTab as ForecastType, filterSign !== 'all' && filterSign !== 'general' ? filterSign : null, new Date().toISOString().split('T')[0]));
  };
  if (editingForecast) {
    return <div className="space-y-6" data-id="element-4304">
        <div className="flex items-center justify-between" data-id="element-4305">
          <div data-id="element-4306">
            <h2 className="text-2xl font-bold text-slate-900" data-id="element-4307">
              {forecasts.some(f => f.id === editingForecast.id) ? 'Редактирование прогноза' : 'Новый прогноз'}
            </h2>
            <p className="text-slate-500" data-id="element-4308">Заполните контент прогноза</p>
          </div>
          <div className="flex items-center gap-3" data-id="element-4309">
            <Button variant="outline" onClick={() => setEditingForecast(null)} data-id="element-4310">
              Отмена
            </Button>
            <Button variant="primary" onClick={handleSaveEdit} data-id="element-4311">
              Сохранить
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4312">
          <div className="lg:col-span-2 space-y-6" data-id="element-4313">
            <Card padding="lg" className="space-y-6" data-id="element-4314">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3" data-id="element-4315">
                <h3 className="text-lg font-bold text-slate-900" data-id="element-4316">
                  Основная информация
                </h3>
                {editingForecast.ai_generated && <Badge variant="neutral" size="sm" className="bg-purple-50 text-purple-700 border-purple-200" data-id="element-4317">
                    <Sparkles className="w-3 h-3 mr-1" data-id="element-4318" /> Сгенерировано AI
                  </Badge>}
              </div>

              <div className="space-y-4" data-id="element-4319">
                <div data-id="element-4320">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4321">
                    Название (внутреннее) *
                  </label>
                  <Input value={editingForecast.title} onChange={e => setEditingForecast({
                  ...editingForecast,
                  title: e.target.value
                })} data-id="element-4322" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4323">
                  <div data-id="element-4324">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4325">
                      Тип *
                    </label>
                    <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={editingForecast.forecast_type} onChange={e => setEditingForecast({
                    ...editingForecast,
                    forecast_type: e.target.value as ForecastType
                  })} data-id="element-4326">
                      {Object.entries(FORECAST_TYPE_LABELS).map(([key, val]) => <option key={key} value={key} data-id="element-4327">
                            {val.ru}
                          </option>)}
                    </select>
                  </div>
                  <div data-id="element-4328">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4329">
                      Знак зодиака *
                    </label>
                    <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={editingForecast.scope === 'general' ? 'general' : editingForecast.zodiac_sign || ''} onChange={e => {
                    const val = e.target.value;
                    if (val === 'general') {
                      setEditingForecast({
                        ...editingForecast,
                        scope: 'general',
                        zodiac_sign: null
                      });
                    } else {
                      setEditingForecast({
                        ...editingForecast,
                        scope: 'zodiac',
                        zodiac_sign: val as ZodiacSign
                      });
                    }
                  }} data-id="element-4330">
                      <option value="general" data-id="element-4331">Общий прогноз (для всех)</option>
                      {ZODIAC_SIGNS.map(z => <option key={z.key} value={z.key} data-id="element-4332">
                          {z.symbol} {z.ru}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-4333">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4334">
                      Язык *
                    </label>
                    <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={editingForecast.lang} onChange={e => setEditingForecast({
                    ...editingForecast,
                    lang: e.target.value as SupportedLang
                  })} data-id="element-4335">
                      <option value="ru" data-id="element-4336">Русский</option>
                      <option value="en" data-id="element-4337">English</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4338">
                  <div data-id="element-4339">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4340">
                      Дата начала действия *
                    </label>
                    <Input type="date" value={editingForecast.date_start} onChange={e => setEditingForecast({
                    ...editingForecast,
                    date_start: e.target.value
                  })} data-id="element-4341" />
                  </div>
                  <div data-id="element-4342">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4343">
                      Дата окончания действия *
                    </label>
                    <Input type="date" value={editingForecast.date_end} onChange={e => setEditingForecast({
                    ...editingForecast,
                    date_end: e.target.value
                  })} data-id="element-4344" />
                  </div>
                </div>
              </div>
            </Card>

            <Card padding="lg" className="space-y-6" data-id="element-4345">
              <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-3" data-id="element-4346">
                Текст прогноза
              </h3>

              <div className="space-y-6" data-id="element-4347">
                <div data-id="element-4348">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4349">
                    Общий прогноз *
                  </label>
                  <textarea className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[120px]" value={editingForecast.content.general} onChange={e => setEditingForecast({
                  ...editingForecast,
                  content: {
                    ...editingForecast.content,
                    general: e.target.value
                  }
                })} data-id="element-4350" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-4351">
                  <div className="space-y-2" data-id="element-4352">
                    <div className="flex items-center justify-between" data-id="element-4353">
                      <label className="block text-sm font-medium text-slate-700" data-id="element-4354">
                        Любовь
                      </label>
                      <div className="flex items-center gap-2" data-id="element-4355">
                        <span className="text-xs text-slate-500" data-id="element-4356">
                          {editingForecast.content.love_percent}%
                        </span>
                        <input type="range" min="0" max="100" step="5" className="w-24" value={editingForecast.content.love_percent} onChange={e => setEditingForecast({
                        ...editingForecast,
                        content: {
                          ...editingForecast.content,
                          love_percent: parseInt(e.target.value)
                        }
                      })} data-id="element-4357" />
                      </div>
                    </div>
                    <textarea className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px]" value={editingForecast.content.love_text} onChange={e => setEditingForecast({
                    ...editingForecast,
                    content: {
                      ...editingForecast.content,
                      love_text: e.target.value
                    }
                  })} data-id="element-4358" />
                  </div>

                  <div className="space-y-2" data-id="element-4359">
                    <div className="flex items-center justify-between" data-id="element-4360">
                      <label className="block text-sm font-medium text-slate-700" data-id="element-4361">
                        Карьера
                      </label>
                      <div className="flex items-center gap-2" data-id="element-4362">
                        <span className="text-xs text-slate-500" data-id="element-4363">
                          {editingForecast.content.career_percent}%
                        </span>
                        <input type="range" min="0" max="100" step="5" className="w-24" value={editingForecast.content.career_percent} onChange={e => setEditingForecast({
                        ...editingForecast,
                        content: {
                          ...editingForecast.content,
                          career_percent: parseInt(e.target.value)
                        }
                      })} data-id="element-4364" />
                      </div>
                    </div>
                    <textarea className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px]" value={editingForecast.content.career_text} onChange={e => setEditingForecast({
                    ...editingForecast,
                    content: {
                      ...editingForecast.content,
                      career_text: e.target.value
                    }
                  })} data-id="element-4365" />
                  </div>

                  <div className="space-y-2" data-id="element-4366">
                    <div className="flex items-center justify-between" data-id="element-4367">
                      <label className="block text-sm font-medium text-slate-700" data-id="element-4368">
                        Здоровье
                      </label>
                      <div className="flex items-center gap-2" data-id="element-4369">
                        <span className="text-xs text-slate-500" data-id="element-4370">
                          {editingForecast.content.health_percent}%
                        </span>
                        <input type="range" min="0" max="100" step="5" className="w-24" value={editingForecast.content.health_percent} onChange={e => setEditingForecast({
                        ...editingForecast,
                        content: {
                          ...editingForecast.content,
                          health_percent: parseInt(e.target.value)
                        }
                      })} data-id="element-4371" />
                      </div>
                    </div>
                    <textarea className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px]" value={editingForecast.content.health_text} onChange={e => setEditingForecast({
                    ...editingForecast,
                    content: {
                      ...editingForecast.content,
                      health_text: e.target.value
                    }
                  })} data-id="element-4372" />
                  </div>

                  <div className="space-y-2" data-id="element-4373">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4374">
                      Рекомендация дня/недели
                    </label>
                    <textarea className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px]" value={editingForecast.content.recommendation} onChange={e => setEditingForecast({
                    ...editingForecast,
                    content: {
                      ...editingForecast.content,
                      recommendation: e.target.value
                    }
                  })} data-id="element-4375" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4376">
                  <div data-id="element-4377">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4378">
                      Настроение
                    </label>
                    <Input value={editingForecast.content.mood} onChange={e => setEditingForecast({
                    ...editingForecast,
                    content: {
                      ...editingForecast.content,
                      mood: e.target.value
                    }
                  })} data-id="element-4379" />
                  </div>
                  <div data-id="element-4380">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4381">
                      Цвет удачи
                    </label>
                    <Input value={editingForecast.content.lucky_color} onChange={e => setEditingForecast({
                    ...editingForecast,
                    content: {
                      ...editingForecast.content,
                      lucky_color: e.target.value
                    }
                  })} data-id="element-4382" />
                  </div>
                  <div data-id="element-4383">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4384">
                      Число удачи
                    </label>
                    <Input type="number" value={editingForecast.content.lucky_number} onChange={e => setEditingForecast({
                    ...editingForecast,
                    content: {
                      ...editingForecast.content,
                      lucky_number: parseInt(e.target.value) || 0
                    }
                  })} data-id="element-4385" />
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div className="space-y-6" data-id="element-4386">
            <Card padding="lg" className="space-y-6" data-id="element-4387">
              <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-3" data-id="element-4388">
                Публикация
              </h3>

              <div className="space-y-4" data-id="element-4389">
                <div data-id="element-4390">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4391">
                    Статус
                  </label>
                  <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={editingForecast.status} onChange={e => setEditingForecast({
                  ...editingForecast,
                  status: e.target.value as CmsStatus
                })} data-id="element-4392">
                    {Object.entries(STATUS_LABELS).map(([key, val]) => <option key={key} value={key} data-id="element-4393">
                        {val.ru}
                      </option>)}
                  </select>
                </div>

                <div data-id="element-4394">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4395">
                    Дата авто-публикации
                  </label>
                  <Input type="datetime-local" value={editingForecast.publish_at ? editingForecast.publish_at.slice(0, 16) : ''} onChange={e => setEditingForecast({
                  ...editingForecast,
                  publish_at: e.target.value ? new Date(e.target.value).toISOString() : null
                })} data-id="element-4396" />
                </div>

                <div data-id="element-4397">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4398">
                    Дата авто-архивации
                  </label>
                  <Input type="datetime-local" value={editingForecast.hide_at ? editingForecast.hide_at.slice(0, 16) : ''} onChange={e => setEditingForecast({
                  ...editingForecast,
                  hide_at: e.target.value ? new Date(e.target.value).toISOString() : null
                })} data-id="element-4399" />
                </div>
              </div>
            </Card>

            <Card padding="lg" className="space-y-6" data-id="element-4400">
              <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-3" data-id="element-4401">
                Маркетинг (CTA)
              </h3>

              <div className="space-y-4" data-id="element-4402">
                <div data-id="element-4403">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4404">
                    Текст кнопки (опционально)
                  </label>
                  <Input value={editingForecast.content.cta_text || ''} onChange={e => setEditingForecast({
                  ...editingForecast,
                  content: {
                    ...editingForecast.content,
                    cta_text: e.target.value || null
                  }
                })} placeholder="Например: Заказать полный разбор" data-id="element-4405" />
                </div>

                <div data-id="element-4406">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4407">
                    Ссылка кнопки
                  </label>
                  <Input value={editingForecast.content.cta_link || ''} onChange={e => setEditingForecast({
                  ...editingForecast,
                  content: {
                    ...editingForecast.content,
                    cta_link: e.target.value || null
                  }
                })} placeholder="/services/natal" data-id="element-4408" />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-4409">
      {renderTableHeader('Управление прогнозами', filteredForecasts.length, () => success('Экспорт прогнозов начат'), handleCreateNew, 'Создать прогноз')}

      <div className="flex flex-col sm:flex-row gap-4 justify-between" data-id="element-4410">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} variant="pills" data-id="element-4411" />

        <div className="flex items-center gap-3 flex-wrap" data-id="element-4412">
          <div className="relative w-full sm:w-48" data-id="element-4413">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-4414" />
            <input type="text" placeholder="Поиск..." className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-4415" />
          </div>

          <select className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={filterSign} onChange={e => setFilterSign(e.target.value as any)} data-id="element-4416">
            <option value="all" data-id="element-4417">Все знаки</option>
            <option value="general" data-id="element-4418">Общие (без знака)</option>
            {ZODIAC_SIGNS.map(z => <option key={z.key} value={z.key} data-id="element-4419">
                {z.symbol} {z.ru}
              </option>)}
          </select>

          <select className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={filterStatus} onChange={e => setFilterStatus(e.target.value as any)} data-id="element-4420">
            <option value="all" data-id="element-4421">Все статусы</option>
            {Object.entries(STATUS_LABELS).map(([key, val]) => <option key={key} value={key} data-id="element-4422">
                {val.ru}
              </option>)}
          </select>
        </div>
      </div>

      <Card padding="none" className="overflow-hidden" data-id="element-4423">
        <div className="overflow-x-auto" data-id="element-4424">
          <table className="w-full text-left border-collapse" data-id="element-4425">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-4426">
              <tr data-id="element-4427">
                <th className="px-4 py-3 font-medium" data-id="element-4428">Прогноз</th>
                <th className="px-4 py-3 font-medium" data-id="element-4429">Период</th>
                <th className="px-4 py-3 font-medium" data-id="element-4430">Знак</th>
                <th className="px-4 py-3 font-medium" data-id="element-4431">Статус</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-4432">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-4433">
              {filteredForecasts.length > 0 ? filteredForecasts.map(forecast => {
              const statusInfo = STATUS_LABELS[forecast.status];
              return <tr key={forecast.id} className="hover:bg-slate-50 transition-colors group" data-id="element-4434">
                      <td className="px-4 py-3" data-id="element-4435">
                        <div className="flex items-center gap-2" data-id="element-4436">
                          <div className="font-medium text-slate-900 text-sm" data-id="element-4437">
                            {forecast.title}
                          </div>
                          {forecast.ai_generated && <Sparkles className="w-3.5 h-3.5 text-purple-500" title="Сгенерировано AI" data-id="element-4438" />}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate max-w-xs" data-id="element-4439">
                          {forecast.content.general.substring(0, 60)}...
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-4440">
                        <div className="flex items-center gap-1.5 text-sm text-slate-700" data-id="element-4441">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" data-id="element-4442" />
                          {formatDateRu(forecast.date_start)}
                          {forecast.date_start !== forecast.date_end && ` — ${formatDateRu(forecast.date_end)}`}
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-4443">
                        {forecast.scope === 'general' ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600" data-id="element-4444">
                            Общий
                          </span> : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100" data-id="element-4445">
                            {getZodiacSymbol(forecast.zodiac_sign!)}{' '}
                            {getZodiacLabel(forecast.zodiac_sign!)}
                          </span>}
                      </td>
                      <td className="px-4 py-3" data-id="element-4446">
                        <Badge variant={statusInfo.variant as any} size="sm" data-id="element-4447">
                          {statusInfo.ru}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-4448">
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-4449">
                          {forecast.status === 'draft' && <Button variant="ghost" size="sm" onClick={() => handleStatusChange(forecast.id, 'published')} title="Опубликовать" data-id="element-4450">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4451" />
                            </Button>}
                          <Button variant="ghost" size="sm" onClick={() => handleDuplicate(forecast)} title="Дублировать" data-id="element-4452">
                            <Copy className="w-4 h-4 text-slate-400" data-id="element-4453" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setEditingForecast(forecast)} title="Редактировать" data-id="element-4454">
                            <Edit2 className="w-4 h-4 text-blue-500" data-id="element-4455" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(forecast.id)} title="Удалить" data-id="element-4456">
                            <Trash2 className="w-4 h-4 text-rose-500" data-id="element-4457" />
                          </Button>
                        </div>
                      </td>
                    </tr>;
            }) : <tr data-id="element-4458">
                  <td colSpan={5} className="px-4 py-12 text-center text-slate-500" data-id="element-4459">
                    <div className="flex flex-col items-center justify-center" data-id="element-4460">
                      <AlertCircle className="w-8 h-8 text-slate-300 mb-2" data-id="element-4461" />
                      <p data-id="element-4462">Прогнозы не найдены</p>
                      <Button variant="outline" size="sm" className="mt-4" onClick={handleCreateNew} data-id="element-4463">
                        Создать прогноз
                      </Button>
                    </div>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}